import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';

import { domainOf, isFreeMail, type Supplier, supplierForSender, tariffDate } from './types';
import { useCreateSupplier, useFromAttachment, useSuppliers } from './useTariffs';

const NEW = '__nuevo__';

/** `pedidos.bosch.es` → `bosch.es`: what to ask the API for, so the supplier is found
 * even past the first page of the list (its search reads the domains too). */
function searchableDomain(domain: string): string {
  return domain.split('.').slice(-2).join('.');
}

export interface TariffAttachment {
  emailId: string;
  attachmentId: string;
  filename: string;
  /** The sender of the email, to find their supplier. */
  fromAddress?: string | null;
}

/**
 * «Cargar como tarifa» (contract v10.48): the Excel a supplier sent goes to «Documentos»
 * without downloading it and uploading it again. The person says whose it is and which of
 * their tariffs; the supplier whose domain is the sender's comes chosen. A supplier that is
 * not there yet is created here, with the sender's domain. Then the screen of the new
 * version takes over: the columns, what changes, publish.
 */
export function LoadAsTariffDialog({
  attachment,
  onClose,
}: {
  attachment: TariffAttachment;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const senderDomain = domainOf(attachment.fromAddress);
  const domainToKeep = senderDomain && !isFreeMail(senderDomain) ? senderDomain : null;
  const suppliers = useSuppliers();
  const bySender = useSuppliers(
    domainToKeep ? searchableDomain(domainToKeep) : '',
    0,
    domainToKeep !== null,
  );
  const createSupplier = useCreateSupplier();
  const load = useFromAttachment();

  const guessed = useMemo(
    () => supplierForSender(bySender.data?.items ?? [], attachment.fromAddress),
    [bySender.data, attachment.fromAddress],
  );
  // The first page of the list, and the sender's supplier when it is past it.
  const all = useMemo(() => {
    const listed = suppliers.data?.items ?? [];
    return guessed && !listed.some((each) => each.id === guessed.id)
      ? [...listed, guessed].sort((a, b) => a.name.localeCompare(b.name, 'es'))
      : listed;
  }, [suppliers.data, guessed]);
  const [picked, setPicked] = useState<string | null>(null);
  const supplierId = picked ?? guessed?.id ?? '';
  const supplier: Supplier | undefined = all.find((each) => each.id === supplierId);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [listChoice, setListChoice] = useState<string | null>(null);
  const [newListName, setNewListName] = useState('Tarifa general');

  const lists = supplier?.price_lists ?? [];
  const listId = listChoice ?? lists[0]?.id ?? NEW;
  const chosenList = lists.find((each) => each.id === listId);

  const busy = createSupplier.isPending || load.isPending;
  const failure = createSupplier.error ?? load.error;
  const creating = supplierId === NEW;
  // A tariff with a version waiting is refused by the back (409): one at a time, so two
  // people do not publish over each other.
  const ready = creating
    ? newSupplierName.trim() !== '' && newListName.trim() !== ''
    : supplierId !== '' && (listId !== NEW || newListName.trim() !== '') && !chosenList?.pending;

  async function submit() {
    try {
      let targetSupplier = supplierId;
      if (creating) {
        const created = await createSupplier.mutateAsync({
          name: newSupplierName.trim(),
          domains: domainToKeep ? [domainToKeep] : [],
        });
        targetSupplier = created.id;
        setPicked(created.id);
      }
      const version = await load.mutateAsync({
        email_id: attachment.emailId,
        attachment_id: attachment.attachmentId,
        ...(creating || listId === NEW
          ? { supplier_id: targetSupplier, price_list_name: newListName.trim() }
          : { price_list_id: listId }),
      });
      onClose();
      void navigate(`/documentos/tarifas/${version.id}`);
    } catch {
      // Shown under the form from the mutation's own error.
    }
  }

  return (
    <Dialog
      title="Cargar como tarifa"
      description={attachment.filename}
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={!ready || busy}>
            {busy ? 'Cargando…' : 'Cargar'}
          </Button>
        </>
      }
    >
      {suppliers.isPending ? <Spinner variant="block" label="Cargando proveedores" /> : null}
      {suppliers.isError ? (
        <ErrorMessage error={suppliers.error}>
          No se pudo cargar la lista de proveedores
        </ErrorMessage>
      ) : null}
      {suppliers.data ? (
        <div className="flex flex-col gap-4">
          <Select
            label="Proveedor"
            value={supplierId}
            onChange={(event) => {
              setPicked(event.target.value);
              setListChoice(null);
            }}
            hint={
              guessed && supplierId === guessed.id
                ? 'Elegido por la dirección de quien lo manda.'
                : undefined
            }
          >
            <option value="" disabled>
              Elige el proveedor
            </option>
            {all.map((each) => (
              <option key={each.id} value={each.id}>
                {each.name}
              </option>
            ))}
            <option value={NEW}>Proveedor nuevo…</option>
          </Select>

          {creating ? (
            <Input
              label="Nombre del proveedor"
              value={newSupplierName}
              onChange={(event) => setNewSupplierName(event.target.value)}
              maxLength={200}
              hint={
                domainToKeep
                  ? `Se guarda con ${domainToKeep}, para reconocer sus correos la próxima vez.`
                  : undefined
              }
            />
          ) : null}

          {supplier && lists.length > 0 ? (
            <Select
              label="Tarifa"
              value={listId}
              onChange={(event) => setListChoice(event.target.value)}
              hint="Una versión nueva de la tarifa que elijas: la vigente sigue hasta que publiques esta."
            >
              {lists.map((each) => (
                <option key={each.id} value={each.id}>
                  {each.current
                    ? `${each.name} (vigente del ${tariffDate(each.current.tariff_date)})`
                    : each.name}
                </option>
              ))}
              <option value={NEW}>Tarifa nueva…</option>
            </Select>
          ) : null}

          {creating || (supplierId !== '' && listId === NEW) ? (
            <Input
              label="Nombre de la tarifa"
              value={newListName}
              onChange={(event) => setNewListName(event.target.value)}
              maxLength={120}
              hint="Por ejemplo «Tarifa general», «Máquinas» o «Accesorios»."
            />
          ) : null}

          {chosenList?.pending ? (
            <Callout tone="warn" compact>
              Esta tarifa ya tiene una versión sin publicar.{' '}
              <Link className="link" to={`/documentos/tarifas/${chosenList.pending.id}`}>
                Ábrela
              </Link>{' '}
              y publícala o descártala antes de cargar otra.
            </Callout>
          ) : null}

          {failure ? (
            <ErrorMessage error={failure}>No se pudo cargar la tarifa</ErrorMessage>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
